import { usersService } from '@/lib/api';
import { UserData } from '../types/user';
import { UserOrganizationMembership } from '../types/organization';

// Re-export API service for backward compatibility
export { usersService } from '@/lib/api';

/**
 * Check if an email already exists in the system
 * Note: This functionality should be handled by the auth service during registration
 */
export const checkIfEmailExists = async (email: string): Promise<boolean> => {
  try {
    // Attempt to get user by email - this will return 404 if not found
    const response = await usersService.getUserByEmail(email);
    return response.success;
  } catch (error) {
    console.error('Error checking email existence:', error);
    return false; // Default to assuming email doesn't exist
  }
};

/**
 * Get user data by ID
 * Note: This maps to the backend user profile API
 */
export const getUserData = async (userId: string): Promise<UserData | null> => {
  try {
    const response = await usersService.getUserById(userId);
    
    if (!response.success || !response.data) {
      return null;
    }

    // Map backend user profile to frontend UserData format
    const userProfile = response.data;
    return {
      uid: userProfile.id,
      email: userProfile.email,
      displayName: userProfile.display_name || null,
      photoURL: userProfile.photo_url || null,
      emailVerified: userProfile.email_verified,
      createdAt: new Date(userProfile.created_at),
      updatedAt: new Date(userProfile.updated_at),
      timezone: userProfile.timezone,
      // Note: Organizations are handled separately in the new architecture
      organizations: {},
      currentOrganizationId: undefined
    };
  } catch (error) {
    console.error('Error fetching user data:', error);
    return null;
  }
};

/**
 * Update user data
 * Note: Maps to the backend user profile update API
 */
export const updateUserData = async (
  _userId: string,
  updates: Partial<UserData>
): Promise<void> => {
  try {
    // Map frontend UserData fields to backend API format
    const backendUpdates: Record<string, unknown> = {};
    
    if (updates.displayName !== undefined) {
      backendUpdates.display_name = updates.displayName;
    }
    if (updates.timezone !== undefined) {
      backendUpdates.timezone = updates.timezone;
    }
    
    // Handle preferences or other custom fields
    if (updates.currentOrganizationId !== undefined) {
      backendUpdates.preferences = {
        currentOrganizationId: updates.currentOrganizationId
      };
    }

    const response = await usersService.updateCurrentUserProfile(backendUpdates);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to update user data');
    }
  } catch (error) {
    console.error('Error updating user data:', error);
    throw error;
  }
};

// ==============================
// ORGANIZATION MEMBERSHIP OPERATIONS
// Note: These functions are stubs that need to be implemented when
// organization membership APIs are available in the backend
// ==============================

/**
 * Add user to organization
 * TODO: Implement when organization membership API is available
 */
export const addUserToOrganization = async (
  _userId: string,
  _organizationId: string,
  _role: 'owner' | 'admin' | 'member'
): Promise<void> => {
  // This would be implemented with an organization membership API
  // For now, throw an error to indicate it's not implemented
  throw new Error('Organization membership operations are not yet implemented in the backend API. This requires organization membership endpoints.');
};

/**
 * Remove user from organization
 * TODO: Implement when organization membership API is available
 */
export const removeUserFromOrganization = async (
  _userId: string,
  _organizationId: string
): Promise<void> => {
  throw new Error('Organization membership operations are not yet implemented in the backend API. This requires organization membership endpoints.');
};

/**
 * Get user organizations
 */
export const getUserOrganizations = async (userId: string): Promise<UserOrganizationMembership[]> => {
  try {
    // Note: The new API only supports getting current user's organizations
    // For getting other user's organizations, we would need a different endpoint
    const response = await usersService.getUserOrganizations();
    
    if (!response.success || !response.data) {
      return [];
    }

    // Map from backend format to frontend UserOrganizationMembership format
    return response.data.organizations.map(org => ({
      organizationId: org.organizationId,
      role: org.role,
      joinedAt: new Date(org.joinedAt),
      status: 'active' // Default status
    }));
  } catch (error) {
    console.error('Error fetching user organizations:', error);
    return [];
  }
};

/**
 * Get user role in organization
 */
export const getUserRoleInOrganization = async (
  _userId: string, // Note: Backend API only supports current user
  organizationId: string
): Promise<'owner' | 'admin' | 'member' | null> => {
  try {
    // Note: The new API only supports getting current user's role
    // For getting other user's roles, we would need a different endpoint
    const response = await usersService.getUserRoleInOrganization(organizationId);
    
    if (!response.success || !response.data) {
      return null;
    }

    return response.data.role;
  } catch (error) {
    console.error('Error fetching user role in organization:', error);
    return null;
  }
};

/**
 * Check if user is organization member
 */
export const isUserOrganizationMember = async (
  _userId: string, // Note: Backend API only supports current user
  organizationId: string
): Promise<boolean> => {
  try {
    // Note: The new API only supports checking current user's membership
    // For checking other user's membership, we would need a different endpoint
    const response = await usersService.checkMembershipStatus(organizationId);
    
    if (!response.success || !response.data) {
      return false;
    }

    return response.data.isMember;
  } catch (error) {
    console.error('Error checking user organization membership:', error);
    return false;
  }
};

/**
 * Update user organization role
 * TODO: Implement when organization membership API is available
 */
export const updateUserOrganizationRole = async (
  _userId: string,
  _organizationId: string,
  _role: 'owner' | 'admin' | 'member'
): Promise<void> => {
  throw new Error('Organization membership operations are not yet implemented in the backend API. This requires organization membership endpoints.');
};

/**
 * Get users in organization
 * TODO: Implement when organization membership API is available
 */
export const getUsersInOrganization = async (memberIds: string[]): Promise<(UserData & { organizationRole?: 'owner' | 'admin' | 'member' })[]> => {
  if (memberIds.length === 0) {
    return [];
  }

  // This would need to be implemented with proper organization membership API
  // For now, try to fetch individual users (this is inefficient but provides some functionality)
  const users: (UserData & { organizationRole?: 'owner' | 'admin' | 'member' })[] = [];
  
  try {
    for (const userId of memberIds.slice(0, 10)) { // Limit to 10 to avoid overwhelming the API
      const userData = await getUserData(userId);
      if (userData) {
        users.push(userData);
      }
    }
  } catch (error) {
    console.error('Error fetching organization users:', error);
  }
  
  return users;
};

// ==============================
// HELPER FUNCTIONS
// ==============================

/**
 * Map backend user profile to frontend UserData
 */
export const mapBackendUserToUserData = (backendUser: Record<string, unknown>): UserData => {
  return {
    uid: backendUser.id as string,
    email: backendUser.email as string,
    displayName: (backendUser.display_name as string) || null,
    photoURL: (backendUser.photo_url as string) || null,
    emailVerified: backendUser.email_verified as boolean,
    createdAt: new Date(backendUser.created_at as string),
    updatedAt: new Date(backendUser.updated_at as string),
    timezone: backendUser.timezone as string,
    // Organizations would be populated from separate API calls
    organizations: {},
    currentOrganizationId: (backendUser.preferences as Record<string, unknown>)?.currentOrganizationId as string
  };
};

/**
 * Map backend UserProfile to frontend UserData
 */
export const mapUserProfileToUserData = (userProfile: any): UserData => {
  return {
    uid: userProfile.id,
    email: userProfile.email,
    displayName: userProfile.display_name || null,
    photoURL: userProfile.photo_url || null,
    emailVerified: userProfile.email_verified || false,
    createdAt: new Date(userProfile.created_at),
    updatedAt: new Date(userProfile.updated_at),
    timezone: userProfile.timezone,
    // Organizations would be populated from separate API calls
    organizations: {},
    currentOrganizationId: userProfile.preferences?.currentOrganizationId
  };
};

/**
 * Get current user data using the API service
 */
export const getCurrentUserData = async (): Promise<UserData | null> => {
  try {
    const response = await usersService.getCurrentUserProfile();
    
    if (!response.success || !response.data) {
      return null;
    }

    return mapUserProfileToUserData(response.data);
  } catch (error) {
    console.error('Error fetching current user data:', error);
    return null;
  }
};

/**
 * Update current user data using the API service
 */
export const updateCurrentUserData = async (updates: Partial<UserData>): Promise<UserData | null> => {
  try {
    // Map frontend UserData fields to backend API format
    const backendUpdates: Record<string, any> = {};
    
    if (updates.displayName !== undefined) {
      backendUpdates.display_name = updates.displayName;
    }
    if (updates.timezone !== undefined) {
      backendUpdates.timezone = updates.timezone;
    }
    
    // Handle preferences or other custom fields
    if (updates.currentOrganizationId !== undefined) {
      if (!backendUpdates.preferences) {
        backendUpdates.preferences = {};
      }
      (backendUpdates.preferences as Record<string, any>).currentOrganizationId = updates.currentOrganizationId;
    }

    const response = await usersService.updateCurrentUserProfile(backendUpdates);
    
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to update user data');
    }

    return mapUserProfileToUserData(response.data);
  } catch (error) {
    console.error('Error updating current user data:', error);
    throw error;
  }
};